import React from "react";
import { useNavigate } from "react-router-dom";
import { Bot, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

const Section = ({ title, children }) => (
  <div className="mt-8">
    <h2 className="font-display text-2xl tracking-tight mb-3 text-primary">{title}</h2>
    <div className="space-y-2 text-muted-foreground leading-relaxed">{children}</div>
  </div>
);

export default function Privacy() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background text-foreground grain" data-testid="privacy-page">
      <nav className="flex items-center justify-between px-6 md:px-12 py-5 border-b border-white/10 bg-[#0a0a0a] sticky top-0 z-50" style={{ paddingTop: "calc(1.25rem + env(safe-area-inset-top))" }}>
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 bg-primary rounded-sm flex items-center justify-center"><Bot className="h-5 w-5 text-white" /></div>
          <span className="font-display text-2xl tracking-wide">TRADIE<span className="text-primary">DESK</span></span>
        </div>
        <Button data-testid="privacy-back-btn" variant="ghost" onClick={() => navigate(-1)} className="rounded-sm gap-2 text-foreground hover:text-primary hover:bg-secondary">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
      </nav>
      <div className="max-w-3xl mx-auto px-6 md:px-8 py-12">
        <div className="bg-[#18181b] border border-white/10 rounded-sm p-6 md:p-10">
          <h1 className="font-display text-3xl md:text-4xl tracking-tight mb-2">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground">Last updated: June 2026</p>

          <Section title="Who we are">
            <p>TradieDesk provides an AI receptionist ("Ava") that helps trade and service businesses answer customer enquiries, capture leads and arrange call-backs. This policy explains what data we collect and how we handle it.</p>
          </Section>

          <Section title="Information we collect">
            <p>• <span className="text-foreground">Account data</span> from business owners who sign up (name, email, business details) via Google sign-in.</p>
            <p>• <span className="text-foreground">Customer enquiry data</span> that a customer voluntarily provides while chatting with Ava (such as name, phone number, email, job description and address) so the business can respond.</p>
            <p>• <span className="text-foreground">Messaging data</span> when a business connects a Facebook Page: we receive the messages customers send to that Page and the sender's page-scoped ID (PSID) so Ava can reply. We do not receive your Facebook password or friends list.</p>
            <p>• <span className="text-foreground">Usage data</span> such as chat volumes used to meter AI credits.</p>
          </Section>

          <Section title="How we use it">
            <p>We use this data solely to operate the service: to let Ava answer enquiries, capture and organise leads and bookings for the business, send the business notifications, and meter usage. We do not sell your personal data.</p>
          </Section>

          <Section title="Facebook / Meta data">
            <p>When a business connects its Facebook Page, we store a Page access token and the Page's incoming Messenger messages to enable automated replies. Tokens are stored securely and used only to send and receive messages on that Page's behalf. A business can disconnect a Page at any time from the Connect tab, which stops all further processing.</p>
          </Section>

          <Section title="Data retention & deletion">
            <p>We keep data only as long as needed to provide the service. Businesses can delete leads, bookings and their account from within the dashboard. To request deletion of any data we hold about you, see our <a href="/data-deletion" className="text-primary underline">Data Deletion instructions</a> or email us.</p>
          </Section>

          <Section title="Contact">
            <p>Questions about this policy or your data? Email <a href="mailto:hello@tradiedesk.net" className="text-primary underline">hello@tradiedesk.net</a>.</p>
          </Section>
        </div>
      </div>
    </div>
  );
}
